// Testimonianze reali. Fonte unica: usate dalla home e dalle pagine campagna.

export interface Testimonial {
  quote: string;
  name: string;
  role: string;
  /**
   * Ragione sociale dello studio, per esteso. Va scritta come si chiama oggi:
   * lo Studio Tributario Bracciali si è rinominato Skilta a gennaio 2023.
   */
  company: string;
  /**
   * Immagine quadrata accanto alla citazione: la foto del professionista o,
   * quando non c'è, il logo dello studio su fondo bianco. Va tenuta dentro il
   * quadrato inscritto nel cerchio (~70% del lato), perché la card la ritaglia
   * tonda: un logo a filo del bordo perde i lati. Senza nessuna delle due il
   * cerchio mostra le iniziali (`initialsOf`).
   */
  image?: string;
}

/** Iniziali di nome e cognome: «Leonardo Raffaele Santoro» dà «LS», non «LR». */
export const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toUpperCase();
};

/** Forma usata dalle card citazione del design system */
export interface QuoteCard {
  quote: string;
  name: string;
  role: string;
  company: string;
  /** Iniziali di riserva, mostrate quando non c'è la foto */
  initials: string;
  image?: string;
}

export const sonnatiTestimonial: Testimonial = {
  quote:
    'Trovanorma è diventato il mio braccio destro. Gli approfondimenti sono più veloci e i pareri prendono forma per iscritto. Noi professionisti tendiamo spesso a fare consulenza verbalmente: è un po’ come andare da uno specialista e uscire senza un referto. Trovanorma mi aiuta a lasciare al cliente qualcosa di concreto.',
  name: 'Patrizia Sonnati',
  role: 'Consulente del lavoro',
  company: 'Skilta S.p.A. SB STP',
  image: '/images/reviews/patrizia_sonnati.webp',
};

export const braccialiTestimonial: Testimonial = {
  quote:
    'Permette di ottenere in pochi secondi risposte precise ed aggiornate a quesiti complessi, con un livello di chiarezza e affidabilità che incide concretamente sul lavoro quotidiano. È uno strumento che affianca il professionista, aiutandolo a lavorare con maggiore sicurezza, velocità ed efficacia.',
  name: 'Gian Luca Bracciali',
  role: 'Commercialista',
  company: 'Skilta S.p.A. SB STP',
  image: '/images/reviews/gianluca_bracciali.webp',
};

/**
 * Verificato sul sito dello studio il 1 settembre 2026: il JSON-LD di
 * studiorussogiuseppe.it si descrive come «Studio Commercialista», e i
 * contenuti sono tutti fiscali e tributari. Il logo è copiato in casa
 * (`public/images/reviews/`) come le foto: niente immagini prese a caldo da
 * un dominio che non controlliamo.
 *
 * Cut del pezzo tra "utilizzo." e "A un collega":
 *  La sua forza sta nella capacità di restituire un testo già pronto per l’uso, unito a una funzione fondamentale per la pratica professionale: l’accesso diretto ai link delle sentenze e delle norme citate. È un assistente di grande valore che mi fa risparmiare tempo prezioso, e vedo con grande favore il continuo ampliamento di nuove funzionalità.
 */
export const russoTestimonial1: Testimonial = {
  quote:
    'Uso Trovanorma ormai quotidianamente e lo reputo uno strumento professionale tra i più completi nel panorama attuale, superiore ad altri servizi analoghi che utilizzo. A un collega che stesse valutando di provarlo consiglierei di testarlo senza esitazioni, perché diventa fin da subito un punto di riferimento insostituibile nello studio.',
  name: 'Giuseppe Russo',
  role: 'Commercialista',
  company: 'Studio Russo Giuseppe',
  image: '/images/reviews/studio_russo_giuseppe.webp',
};

export const russoTestimonial2: Testimonial = {
  quote:
    'La forza di Trovanorma sta nella capacità di restituire un testo già pronto per l’uso, unito a una funzione fondamentale per la pratica professionale: l’accesso diretto ai link delle sentenze e delle norme citate. È un assistente di grande valore che mi fa risparmiare tempo prezioso, e vedo con grande favore il continuo ampliamento di nuove funzionalità.',
  name: 'Giuseppe Russo',
  role: 'Commercialista',
  company: 'Studio Russo Giuseppe',
  image: '/images/reviews/studio_russo_giuseppe.webp',
};

/**
 * Verificato sul profilo iustlab.org/leonardo.raffaele.santoro il 18
 * settembre 2026: avvocato a Matera (via Gattini 10) con sede a Ginosa,
 * attività generalista, dal penale al tributario. Il «specializzato nelle
 * professioni legali» che accompagnava la citazione è il titolo della Scuola di
 * specializzazione per le professioni legali (LUM, 2016), non un'area di
 * pratica: come ruolo va il mestiere, come per gli altri.
 *
 * Niente foto: quella del profilo iustlab non è stata data a noi, e senza il
 * suo consenso non si usa. Il cerchio mostra le iniziali.
 *
 * Nell'originale «consiglierei a colleghi e entranei al settore giuridico»:
 * letto come «estranei», che si lega all'«utilizzo estremamente semplice».
 */
export const santoroTestimonial: Testimonial = {
  quote:
    'Un utilizzo estremamente semplice ma assolutamente performante, risposte concrete e precise e strumenti di ausilio del tutto completi. Un prodotto che consiglierei a colleghi ed estranei al settore giuridico.',
  name: 'Leonardo Raffaele Santoro',
  role: 'Avvocato',
  company: 'Studio Legale Santoro',
};

/**
 * Citazioni mostrate in homepage. Contiene solo testimonianze reali già
 * raccolte: per aggiungerne servono il testo e il consenso del professionista,
 * non vanno inventate per riempire la griglia.
 *
 * **Una voce per mestiere, non tutte.** Queste sono descrittori
 * riutilizzabili, e metterli tutti ovunque non aggiunge fiducia: la toglie,
 * perché si vede che si sta riempiendo uno spazio. In home stanno Santoro,
 * Russo e Sonnati — avvocato, commercialista e consulente del lavoro, nello
 * stesso ordine delle card delle professioni che hanno sopra: ognuna ha chi
 * parla per lei. Bracciali lavora sulle pagine dove il suo mestiere è il tema
 * (/soluzioni/commercialisti, /demo e le funzionalità che non ne scelgono
 * una), Santoro su /soluzioni/avvocati e /soluzioni/studi-legali. Su /invito e
 * /convenzioni stanno Santoro e Bracciali, avvocato e commercialista.
 */
export const HOME_TESTIMONIALS: QuoteCard[] = [
  {
    quote: santoroTestimonial.quote,
    name: santoroTestimonial.name,
    role: santoroTestimonial.role,
    company: santoroTestimonial.company,
    initials: 'LS',
  },
  {
    quote: russoTestimonial1.quote,
    name: russoTestimonial1.name,
    role: russoTestimonial1.role,
    company: russoTestimonial1.company,
    initials: 'GR',
    image: russoTestimonial1.image,
  },
  {
    quote: sonnatiTestimonial.quote,
    name: sonnatiTestimonial.name,
    role: sonnatiTestimonial.role,
    company: sonnatiTestimonial.company,
    initials: 'PS',
    image: sonnatiTestimonial.image,
  },
];
